<template>
  <div class="playback-speed-control" @click.stop>
    <button
      type="button"
      class="playback-speed-trigger"
      aria-haspopup="true"
      :aria-expanded="String(panelVisible)"
      @click="panelVisible = !panelVisible"
    >
      {{ displaySpeed }}
    </button>
    <div v-show="panelVisible" class="playback-speed-panel">
      <button
        v-for="speed in reversedPresets"
        :key="speed"
        type="button"
        class="playback-speed-preset"
        :class="{ active: currentSpeed === speed }"
        @click="selectPreset(speed)"
      >
        {{ speed === 1 ? '正常' : formatSpeed(speed) }}
      </button>
      <div class="playback-speed-custom">
        <div class="playback-speed-custom-header">
          <span>自定义速度</span>
          <output aria-live="polite">{{ formatSpeed(currentSpeed) }}</output>
        </div>
        <div class="playback-speed-custom-controls">
          <button type="button" aria-label="降低播放速度" @click="decreaseSpeed">−</button>
          <input
            type="range"
            :min="min"
            :max="max"
            :step="step"
            :value="currentSpeed"
            :aria-valuetext="formatSpeed(currentSpeed)"
            aria-label="自定义播放速度"
            @input="changeCustomSpeed"
          />
          <button type="button" aria-label="提高播放速度" @click="increaseSpeed">＋</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  PLAYBACK_SPEED_MAX,
  PLAYBACK_SPEED_MIN,
  PLAYBACK_SPEED_PRESETS,
  PLAYBACK_SPEED_STEP,
  formatPlaybackSpeed,
  getStoredPlaybackSpeed,
  normalizePlaybackSpeed,
  savePlaybackSpeed,
} from '@/utils/playbackSpeed'

export default {
  name: 'PlaybackSpeedControl',
  props: {
    value: {
      type: [Number, String],
      default: 1,
    },
  },
  data() {
    return {
      min: PLAYBACK_SPEED_MIN,
      max: PLAYBACK_SPEED_MAX,
      step: PLAYBACK_SPEED_STEP,
      currentSpeed: normalizePlaybackSpeed(this.value),
      panelVisible: false,
      reversedPresets: PLAYBACK_SPEED_PRESETS.slice().reverse(),
    }
  },
  computed: {
    displaySpeed() {
      return this.currentSpeed === 1
        ? '正常'
        : formatPlaybackSpeed(this.currentSpeed)
    },
  },
  watch: {
    value(value) {
      this.currentSpeed = normalizePlaybackSpeed(value)
    },
  },
  mounted() {
    document.addEventListener('click', this.closePanel)
    const storedSpeed = getStoredPlaybackSpeed(this.currentSpeed)
    if (storedSpeed !== this.currentSpeed) {
      this.applySpeed(storedSpeed)
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closePanel)
  },
  methods: {
    formatSpeed: formatPlaybackSpeed,
    closePanel() {
      this.panelVisible = false
    },
    applySpeed(speed, closePanel = false) {
      const value = savePlaybackSpeed(speed)
      this.currentSpeed = value
      this.$emit('input', value)
      this.$emit('change', value)
      if (closePanel) {
        this.closePanel()
      }
    },
    selectPreset(speed) {
      this.applySpeed(speed, true)
    },
    changeCustomSpeed(event) {
      this.applySpeed(event.target.value)
    },
    decreaseSpeed() {
      this.applySpeed(this.currentSpeed - this.step)
    },
    increaseSpeed() {
      this.applySpeed(this.currentSpeed + this.step)
    },
  },
}
</script>

<style lang="less" scoped>
.playback-speed-control {
  position: relative;
  margin: 0 10px;
  color: #fff;
  font-size: 12px;
  line-height: normal;
}

.playback-speed-trigger {
  min-width: 62px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 3px;
  outline: none;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.playback-speed-panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 30;
  width: 208px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.78);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(8px);
}

.playback-speed-preset {
  display: block;
  width: 100%;
  height: 34px;
  padding: 0 12px;
  border: 0;
  outline: none;
  color: #fff;
  background: transparent;
  cursor: pointer;

  &:hover,
  &:focus,
  &.active {
    color: #1795ff;
    background: rgba(255, 255, 255, 0.1);
  }
}

.playback-speed-custom {
  padding: 10px 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.playback-speed-custom-header,
.playback-speed-custom-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.playback-speed-custom-header {
  margin-bottom: 8px;

  output {
    color: #1795ff;
  }
}

.playback-speed-custom-controls {
  button {
    display: inline-flex;
    flex: 0 0 26px;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.36);
    border-radius: 4px;
    outline: none;
    color: #fff;
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus {
      border-color: #1795ff;
      color: #1795ff;
    }
  }

  input {
    flex: 1;
    min-width: 0;
    margin: 0 8px;
    accent-color: #1795ff;
    cursor: pointer;
  }
}
</style>
